import type { Database } from "../storage.js";

// Watermarks owned by the local database. Keyed by (k, backend) so a
// single workspace can host more than one backend and each keeps its
// own sync cursors. The container's appliedPushRev lives in-memory on
// the container side; we don't store it here.
//
// pushRev — last DO-side rev successfully pushed to the backend.
//
// initializeSchema() seeds pushRev at 0 in _vfs_watermark for the
// default backend. The schema table is the durability surface;
// readers and writers always go through this module so the SQL
// stays in one place.
//
// `backend` defaults to DEFAULT_BACKEND_ID so older callers that
// only ran one backend (or ran the package against a schema before
// per-backend keying landed) keep working unchanged. The v3 → v4
// schema migration backfills the column on existing rows with the
// same default.
//
// Fetch progress is a `{ rev, path }` cursor. Its rev component is
// still stored in _vfs_watermark for schema compatibility, but callers
// must use readFetchCursor() / writeFetchCursor() so rev and path stay
// consistent.
export type WatermarkKey = "pushRev";

export const DEFAULT_BACKEND_ID = "default";

// Cursor into the remote change stream. `path: null` means `rev`
// is fully drained and the next fetch resumes strictly after that
// rev. A string path means resume inside the same rev after that
// path. The empty string is a real path value, not a sentinel, and
// must not be used to mean "start of rev".
export type ChangeCursor = { rev: number; path: string | null };

export function readWatermark(
  db: Database,
  key: WatermarkKey,
  backend: string = DEFAULT_BACKEND_ID,
): number {
  return (
    db.scalar<number>("SELECT v FROM _vfs_watermark WHERE k = ? AND backend = ?", key, backend) ?? 0
  );
}

function readFetchRev(db: Database, backend: string = DEFAULT_BACKEND_ID): number {
  return (
    db.scalar<number>(
      "SELECT v FROM _vfs_watermark WHERE k = ? AND backend = ?",
      "fetchRev",
      backend,
    ) ?? 0
  );
}

function writeWatermarkValue(
  db: Database,
  key: WatermarkKey | "fetchRev",
  value: number,
  backend: string = DEFAULT_BACKEND_ID,
): void {
  db.run(
    "INSERT INTO _vfs_watermark (k, backend, v) VALUES (?, ?, ?) " +
      "ON CONFLICT(k, backend) DO UPDATE SET v = excluded.v",
    key,
    backend,
    value,
  );
}

function writeFetchCursorPath(
  db: Database,
  path: string | null,
  backend: string = DEFAULT_BACKEND_ID,
): void {
  db.run(
    "INSERT INTO _vfs_fetch_cursor (k, backend, path) VALUES (?, ?, ?) " +
      "ON CONFLICT(k, backend) DO UPDATE SET path = excluded.path",
    "fetch",
    backend,
    path,
  );
}

export function writeWatermark(
  db: Database,
  key: WatermarkKey,
  value: number,
  backend: string = DEFAULT_BACKEND_ID,
): void {
  writeWatermarkValue(db, key, value, backend);
}

export function readFetchCursor(db: Database, backend: string = DEFAULT_BACKEND_ID): ChangeCursor {
  const rev = readFetchRev(db, backend);
  if (rev === 0) return { rev: 0, path: null };
  const path = db.scalar<string | null>(
    "SELECT path FROM _vfs_fetch_cursor WHERE k = ? AND backend = ?",
    "fetch",
    backend,
  );
  return { rev, path: path ?? null };
}

export function readPushCursor(db: Database, backend: string = DEFAULT_BACKEND_ID): ChangeCursor {
  const row = db.one<{ rev: number; path: string | null }>(
    "SELECT rev, path FROM _vfs_push_cursor WHERE k = ? AND backend = ?",
    "push",
    backend,
  );
  const watermark = readWatermark(db, "pushRev", backend);
  if (row === undefined || watermark > row.rev) return { rev: watermark, path: null };
  return { rev: row.rev, path: row.path };
}

export function writePushCursor(
  db: Database,
  cursor: ChangeCursor,
  backend: string = DEFAULT_BACKEND_ID,
): void {
  db.transactionSync(() => {
    db.run(
      "INSERT INTO _vfs_push_cursor (k, backend, rev, path) VALUES (?, ?, ?, ?) " +
        "ON CONFLICT(k, backend) DO UPDATE SET rev = excluded.rev, path = excluded.path",
      "push",
      backend,
      cursor.rev,
      cursor.path,
    );
    writeWatermarkValue(db, "pushRev", cursor.rev, backend);
  });
}

export function writeFetchCursor(
  db: Database,
  cursor: ChangeCursor,
  backend: string = DEFAULT_BACKEND_ID,
): void {
  db.transactionSync(() => {
    writeWatermarkValue(db, "fetchRev", cursor.rev, backend);
    writeFetchCursorPath(db, cursor.path, backend);
  });
}

export function compareChangeCursors(a: ChangeCursor, b: ChangeCursor): number {
  if (a.rev !== b.rev) return a.rev - b.rev;
  if (a.path === b.path) return 0;
  if (a.path === null) return 1;
  if (b.path === null) return -1;
  return a.path < b.path ? -1 : 1;
}

// The latest rev stamped on any DO-side mutation. coalesceChanges
// reads this implicitly via vfs_nodes.rev; the sync layer exposes it
// to callers that want to record the rev component of their next
// cursor.
export function currentRev(db: Database): number {
  return db.scalar<number>("SELECT v FROM vfs_meta WHERE k = 'rev'") ?? 0;
}

// True when push has nothing to send this backend: no live node and no
// tombstone sits past its push cursor. A pull that starts from here
// mints revs that hold only what the backend sent, which is what lets
// those revs be acknowledged (or, within one apply call, treated as
// seen). A cursor partway through a rev is conservatively not empty.
export function pushWindowIsEmpty(db: Database, backend: string = DEFAULT_BACKEND_ID): boolean {
  const pushed = readPushCursor(db, backend);
  if (compareChangeCursors(pushed, { rev: currentRev(db), path: null }) >= 0) return true;
  if (pushed.path !== null) return false;
  return (
    db.one("SELECT 1 AS hit FROM vfs_nodes WHERE rev > ? LIMIT 1", pushed.rev) === undefined &&
    db.one("SELECT 1 AS hit FROM vfs_changes WHERE rev > ? LIMIT 1", pushed.rev) === undefined
  );
}
